import React, { useState, useMemo, useEffect } from 'react';
import Header from '../components/layout/Header';
import { useBu } from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import KPICard from '../components/ui/KPICard';
import { useDispatchBoard } from '../hooks/useDispatch';
import { useRefresh } from '../context/RefreshContext';
import { useAuth } from '../context/AuthContext';
import { useWeather } from '../hooks/useWeather';
import { formatCurrency, toLocalDateStr } from '../utils/formatters';
import apiClient from '../api/apiClient';

const RANGE_OPTIONS = [
  { label: 'Today', days: 1 },
  { label: '3 Day', days: 3 },
  { label: '1 Week', days: 7 },
  { label: '2 Week', days: 14 },
  { label: '3 Week', days: 21 },
  { label: 'Month', days: 30 },
  { label: 'Custom', days: null },
];

const toDateStr = toLocalDateStr;

function getRange(days) {
  if (days === null) return { start: null, end: null };
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + days - 1);
  return { start: toDateStr(start), end: toDateStr(end) };
}

function getDaysBetween(startStr, endStr) {
  const days = [];
  const d = new Date(startStr + 'T00:00:00');
  const endD = new Date(endStr + 'T00:00:00');
  while (d <= endD) {
    days.push(toDateStr(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function formatDayLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${days[d.getDay()]} ${months[d.getMonth()]} ${d.getDate()}`;
}

const STATUS_BADGE = {
  Scheduled: { bg: '#dbeafe', color: '#2563eb' },
  InProgress: { bg: '#fef3c7', color: '#d97706' },
  Completed: { bg: '#dcfce7', color: '#16a34a' },
  Canceled: { bg: '#f1f5f9', color: '#64748b' },
};

function fmtHours(h) {
  if (!h && h !== 0) return '0h';
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
}

function weatherEmoji(code) {
  if (code === 0) return '☀️';
  if (code <= 3)  return '⛅';
  if (code <= 48) return '🌫️';
  if (code <= 55) return '🌦️';
  if (code <= 65) return '🌧️';
  if (code <= 77) return '❄️';
  if (code <= 82) return '🌧️';
  if (code <= 86) return '🌨️';
  return '⛈️';
}

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.Canceled;
  return (
    <span style={{ padding: '0.1rem 0.5rem', borderRadius: '999px', fontSize: '0.65rem', fontWeight: '600', background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
      {status || '\u2014'}
    </span>
  );
}

// ── Time helpers for the schedule chart ─────────────────────────────────
// scheduledDate/appointmentEnd are true UTC timestamps from ServiceTitan - convert through
// Date and use local getters so the chart matches the viewing device's own timezone,
// rather than slicing the UTC string directly (which showed UTC clock time regardless of
// where the dispatcher actually is).
function timeStrToMinutes(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.getHours() * 60 + d.getMinutes();
}
function minutesToLabel(mins) {
  const h = Math.floor(mins / 60), m = ((mins % 60) + 60) % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${m ? ':' + String(m).padStart(2, '0') : ''}${ampm}`;
}

// ── Day Schedule Chart (timeline of a single day's jobs for one BU) ─────
function DayScheduleChart({ jobs }) {
  const [sortBy, setSortBy] = useState('tech'); // 'tech' | 'time'
  const CHART_START = 6 * 60;
  const CHART_END = 21 * 60;
  const span = CHART_END - CHART_START;
  const hourMarks = [];
  for (let m = CHART_START; m <= CHART_END; m += 120) hourMarks.push(m);

  const rows = [...jobs]
    .map(job => {
      const startMin = timeStrToMinutes(job.scheduledDate);
      const endMin = timeStrToMinutes(job.appointmentEnd)
        ?? (startMin != null ? startMin + (job.durationHours || 2.25) * 60 : null);
      return { job, startMin, endMin };
    })
    .sort((a, b) => {
      if (sortBy === 'tech') {
        const techA = a.job.techName || '￿'; // sort "Unassigned" last
        const techB = b.job.techName || '￿';
        const techCmp = techA.localeCompare(techB);
        if (techCmp !== 0) return techCmp;
      }
      return (a.startMin ?? 9999) - (b.startMin ?? 9999);
    });

  // Insert a divider row whenever the tech changes, when grouped by tech.
  let lastTech;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.375rem', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, alignSelf: 'center', marginRight: '0.25rem' }}>Sort:</span>
        <button onClick={() => setSortBy('tech')} style={{ ...st.sortToggleBtn, ...(sortBy === 'tech' ? st.sortToggleBtnActive : {}) }}>By Tech</button>
        <button onClick={() => setSortBy('time')} style={{ ...st.sortToggleBtn, ...(sortBy === 'time' ? st.sortToggleBtnActive : {}) }}>By Time</button>
      </div>
      <div style={{ display: 'flex', marginBottom: '0.375rem' }}>
        <div style={{ width: 200, flexShrink: 0 }} />
        <div style={{ flex: 1, position: 'relative', height: 16 }}>
          {hourMarks.map(m => (
            <span key={m} style={{ position: 'absolute', left: `${((m - CHART_START) / span) * 100}%`, transform: 'translateX(-50%)', fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600 }}>
              {minutesToLabel(m)}
            </span>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {rows.map(({ job, startMin, endMin }) => {
          const badge = STATUS_BADGE[job.jobStatus || job.status] || STATUS_BADGE.Canceled;
          const left = startMin != null ? Math.min(100, Math.max(0, ((startMin - CHART_START) / span) * 100)) : 0;
          const width = (startMin != null && endMin != null) ? Math.max(1.5, ((endMin - startMin) / span) * 100) : 0;
          const showTechHeader = sortBy === 'tech' && job.techName !== lastTech;
          lastTech = job.techName;
          return (
            <React.Fragment key={job.id}>
              {showTechHeader && (
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.03em', marginTop: '0.25rem' }}>
                  {job.techName || 'Unassigned'}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: 200, flexShrink: 0, fontSize: '0.7rem', color: '#1e293b', overflow: 'hidden' }}>
                  <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    #{job.jobNumber || job.id}{job.customerName ? ` — ${job.customerName}` : ''}
                  </div>
                  {sortBy !== 'tech' && (
                    <div style={{ color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.techName || 'Unassigned'}</div>
                  )}
                </div>
                <div style={{ flex: 1, position: 'relative', height: 28, background: '#f8fafc', borderRadius: '0.25rem', border: '1px solid #e2e8f0' }}>
                  {startMin != null ? (
                    <div
                      title={`${minutesToLabel(startMin)}${endMin != null ? ' - ' + minutesToLabel(endMin) : ''} · #${job.jobNumber || job.id}`}
                      style={{ position: 'absolute', left: `${left}%`, width: `${width}%`, top: 3, bottom: 3, borderRadius: '0.25rem', background: badge.color, minWidth: 6 }}
                    />
                  ) : (
                    <div style={{ position: 'absolute', left: 6, top: 5, fontSize: '0.65rem', color: '#94a3b8' }}>No time set</div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
        {rows.length === 0 && <div style={{ fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center', padding: '1.5rem' }}>No jobs scheduled this day.</div>}
      </div>
    </div>
  );
}

// ── Day Schedule Modal ───────────────────────────────────────────────────
function DayScheduleModal({ buName, date, jobs, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div style={st.modalOverlay} onClick={onClose}>
      <div style={st.modalCard} onClick={e => e.stopPropagation()}>
        <div style={st.modalHeader}>
          <div>
            <div style={{ fontSize: '0.7rem', color: '#6366f1', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{buName}</div>
            <div style={{ fontSize: '1.05rem', fontWeight: '800', color: '#1e293b' }}>{formatDayLabel(date)} Schedule</div>
          </div>
          <button onClick={onClose} style={st.modalClose}>✕</button>
        </div>
        <div style={{ padding: '1rem', overflow: 'auto' }}>
          <DayScheduleChart jobs={jobs} />
        </div>
      </div>
    </div>
  );
}

// ── Day Column ──────────────────────────────────────────────────────────
function DayColumn({ date, label, jobs, capacityHours = 0, onSelectDay }) {
  const isToday = date === toDateStr(new Date());
  const scheduledHours = jobs.filter(j => (j.jobStatus || j.status) === 'Scheduled').reduce((s, j) => s + (j.durationHours || 2.25), 0);
  const inProgressHours = jobs.filter(j => (j.jobStatus || j.status) === 'InProgress').reduce((s, j) => s + (j.durationHours || 2.25), 0);
  const completedHours = jobs.filter(j => (j.jobStatus || j.status) === 'Completed').reduce((s, j) => s + (j.durationHours || 2.25), 0);
  const needToBookHours = Math.max(0, capacityHours - scheduledHours - inProgressHours - completedHours);
  const revenue = jobs.reduce((s, j) => s + (j.total || 0), 0);

  return (
    <div style={{ ...st.dayCol, ...(isToday ? { borderColor: '#6366f1', background: '#fafaff' } : {}) }}>
      <div
        style={{ ...st.dayHeader, ...(isToday ? { background: '#6366f1', color: '#fff' } : {}), cursor: 'pointer' }}
        onClick={() => onSelectDay(date, jobs)}
        title="Click to view this day's schedule"
      >
        <div style={{ fontWeight: '700', fontSize: '0.8rem' }}>{label}</div>
        <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>{date}</div>
      </div>
      <div style={{ padding: '0.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '700' }}>Capacity (hrs)</span>
          <span style={{ fontSize: '0.8rem', fontWeight: '700' }}>{fmtHours(capacityHours)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#2563eb' }}>Scheduled (hrs)</span>
          <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#2563eb' }}>{fmtHours(scheduledHours)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#d97706' }}>In Progress (hrs)</span>
          <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#d97706' }}>{fmtHours(inProgressHours)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#16a34a' }}>Completed (hrs)</span>
          <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#16a34a' }}>{fmtHours(completedHours)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', background: needToBookHours > 0 ? '#fef2f2' : '#f0fdf4', padding: '0.15rem 0.25rem', borderRadius: '0.25rem' }}>
          <span style={{ fontSize: '0.7rem', color: needToBookHours > 0 ? '#dc2626' : '#16a34a', fontWeight: '700' }}>Need to Book</span>
          <span style={{ fontSize: '0.8rem', fontWeight: '700', color: needToBookHours > 0 ? '#dc2626' : '#16a34a' }}>{fmtHours(needToBookHours)}</span>
        </div>
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '0.25rem', marginTop: '0.25rem', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Revenue</span>
          <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#6366f1' }}>{formatCurrency(revenue)}</span>
        </div>
      </div>
      <div style={{ padding: '0 0.5rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', maxHeight: '200px', overflow: 'auto' }}>
        {jobs.slice(0, 15).map((job, i) => (
          <div key={job.id || i} style={st.dayJobCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: '600', color: '#1e293b' }}>#{job.jobNumber || job.id}</span>
              <StatusBadge status={job.jobStatus || job.status} />
            </div>
            {job.summary && <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '0.125rem' }}>{job.summary.slice(0, 35)}</div>}
            {job.total > 0 && <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#6366f1' }}>{formatCurrency(job.total)}</div>}
          </div>
        ))}
        {jobs.length > 15 && <div style={{ fontSize: '0.65rem', color: '#94a3b8', textAlign: 'center', padding: '0.25rem' }}>+{jobs.length - 15} more</div>}
        {jobs.length === 0 && <div style={{ fontSize: '0.7rem', color: '#cbd5e1', textAlign: 'center', padding: '0.5rem' }}>No jobs</div>}
      </div>
    </div>
  );
}

// ── BU Section with techs ───────────────────────────────────────────────
function BuSection({ buName, buJobs, techs, days, capacityByDay, totalHours = 0, availableHours = 0, bookedHours = 0, capacitySource, buIds = [], onCapacityUpdated, onSelectDay }) {
  const [expanded, setExpanded] = useState(days.length <= 7);
  const [editingTechs, setEditingTechs] = useState(false);
  const [techInput, setTechInput] = useState('');
  const [savingTechs, setSavingTechs] = useState(false);
  const scheduledHours = buJobs.filter(j => (j.jobStatus || j.status) === 'Scheduled').reduce((s, j) => s + (j.durationHours || 2.25), 0);
  const inProgressHours = buJobs.filter(j => (j.jobStatus || j.status) === 'InProgress').reduce((s, j) => s + (j.durationHours || 2.25), 0);
  const completedHours = buJobs.filter(j => (j.jobStatus || j.status) === 'Completed').reduce((s, j) => s + (j.durationHours || 2.25), 0);

  const handleSaveTechs = async (e) => {
    e.stopPropagation();
    const count = parseInt(techInput, 10);
    if (isNaN(count) || count < 0) return;
    setSavingTechs(true);
    try {
      await Promise.all(buIds.map(id => apiClient.put(`/business-units/${id}`, { tech_count: count })));
      setEditingTechs(false);
      if (onCapacityUpdated) onCapacityUpdated();
    } catch (err) { console.error('Failed to save tech count', err); }
    finally { setSavingTechs(false); }
  };
  const needToBookHours = Math.max(0, totalHours - scheduledHours - inProgressHours - completedHours);
  const revenue = buJobs.reduce((s, j) => s + (j.total || 0), 0);

  return (
    <div style={st.buSection}>
      <div style={st.buHeader} onClick={() => setExpanded(!expanded)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#6366f1' }}>{expanded ? '\u25BC' : '\u25B6'}</span>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#1e293b' }}>{buName}</h3>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{buJobs.length} jobs</span>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', marginBottom: '0.1rem' }}>Capacity{capacitySource === 'servicetitan' ? ' (ST)' : ''}</div>
            {editingTechs ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <input
                  autoFocus
                  type="number" min="0" max="99" value={techInput}
                  onChange={e => setTechInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveTechs(e); if (e.key === 'Escape') setEditingTechs(false); }}
                  style={{ width: '52px', padding: '0.2rem 0.3rem', fontSize: '0.8rem', border: '1px solid #6366f1', borderRadius: '0.375rem', textAlign: 'center' }}
                  placeholder="0"
                />
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>techs</span>
                <button onClick={handleSaveTechs} disabled={savingTechs} style={{ padding: '0.15rem 0.4rem', fontSize: '0.7rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>
                  {savingTechs ? '...' : 'Save'}
                </button>
                <button onClick={e => { e.stopPropagation(); setEditingTechs(false); }} style={{ padding: '0.15rem 0.4rem', fontSize: '0.7rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>
                  ✕
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <span
                  style={{ fontSize: '1rem', fontWeight: '800', color: totalHours > 0 ? '#1e293b' : '#94a3b8', cursor: buIds.length > 0 ? 'pointer' : 'default' }}
                  title={buIds.length > 0 ? 'Click to set tech count' : ''}
                  onClick={e => { if (!buIds.length) return; e.stopPropagation(); setTechInput(''); setEditingTechs(true); }}
                >
                  {totalHours > 0 ? fmtHours(totalHours) : 'Set tech count'}
                </span>
                {totalHours > 0 && buIds.length > 0 && (
                  <button title="Edit tech count" onClick={e => { e.stopPropagation(); setTechInput(''); setEditingTechs(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.65rem', padding: '0' }}>✎</button>
                )}
              </div>
            )}
          </div>
          {availableHours > 0 && <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: '#0ea5e9', fontWeight: '600', textTransform: 'uppercase' }}>Available</div>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#0ea5e9' }}>{fmtHours(availableHours)}</div>
          </div>}
          {bookedHours > 0 && <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: '#8b5cf6', fontWeight: '600', textTransform: 'uppercase' }}>Booked</div>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#8b5cf6' }}>{fmtHours(bookedHours)}</div>
          </div>}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Scheduled</div>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#2563eb' }}>{fmtHours(scheduledHours)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>In Progress</div>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#d97706' }}>{fmtHours(inProgressHours)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Completed</div>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#16a34a' }}>{fmtHours(completedHours)}</div>
          </div>
          {totalHours > 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.6rem', color: needToBookHours > 0 ? '#dc2626' : '#16a34a', fontWeight: '700', textTransform: 'uppercase' }}>Need to Book</div>
              <div style={{ fontSize: '1rem', fontWeight: '800', color: needToBookHours > 0 ? '#dc2626' : '#16a34a' }}>{fmtHours(needToBookHours)}</div>
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Techs</div>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#6366f1' }}>{techs.length}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Revenue</div>
            <div style={{ fontSize: '1rem', fontWeight: '800', color: '#22c55e' }}>{formatCurrency(revenue)}</div>
          </div>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0.75rem 1rem' }}>
          {techs.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.375rem' }}>Technicians</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                {techs.map(t => (
                  <div key={t.id} style={st.techChip}>
                    <div style={st.techDot}>{t.name?.[0]?.toUpperCase() || '?'}</div>
                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1e293b' }}>{t.name}</span>
                    {t.position && <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{t.position}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {days.length > 1 ? (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'flex', gap: '0.5rem', minWidth: days.length * 170 }}>
                {days.map(date => {
                  const dayJobs = buJobs.filter(j => {
                    const raw = j.scheduledDate || j.createdOn;
                    return raw && toLocalDateStr(new Date(raw)) === date;
                  });
                  const dayCap = capacityByDay?.[date]?.totalHours ?? 0;
                  return <DayColumn key={date} date={date} label={formatDayLabel(date)} jobs={dayJobs} capacityHours={dayCap} onSelectDay={(d, js) => onSelectDay(buName, d, js)} />;
                })}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              {days.length === 1 && buJobs.length > 0 && (
                <button onClick={() => onSelectDay(buName, days[0], buJobs)} style={st.viewScheduleBtn}>
                  View Schedule Chart
                </button>
              )}
              <table style={st.table}>
                <thead><tr>
                  {['Job #', 'Summary', 'Status', 'Scheduled', 'Hours', 'Total'].map(h => <th key={h} style={st.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {buJobs.length === 0 && <tr><td colSpan={6} style={{ ...st.td, textAlign: 'center', color: '#94a3b8' }}>No jobs</td></tr>}
                  {buJobs.slice(0, 50).map((job, i) => (
                    <tr key={job.id || i} style={i % 2 !== 0 ? { background: '#f8fafc' } : {}}>
                      <td style={st.td}>{job.jobNumber || job.id}</td>
                      <td style={st.td}>{(job.summary || '').slice(0, 40) || '\u2014'}</td>
                      <td style={st.td}><StatusBadge status={job.jobStatus || job.status} /></td>
                      <td style={st.td}>{job.scheduledDate || '\u2014'}</td>
                      <td style={st.td}>{fmtHours(job.durationHours || 2.25)}</td>
                      <td style={st.td}>{formatCurrency(job.total || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function DispatchPage() {
  const [rangeIdx, setRangeIdx] = useState(2); // default: 1 Week
  const [customStart, setCustomStart] = useState(toDateStr(new Date()));
  const [customEnd, setCustomEnd] = useState(toDateStr(new Date()));
  const { selectedBu } = useBu();
  const { myAccounts } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(null); // { buName, date, jobs } | null
  const { triggerRefresh } = useRefresh();

  // Non-Custom ranges are relative to "today" - without this, a tab left open across
  // midnight keeps computing the range from whatever day it happened to mount on, while
  // isToday() (evaluated fresh on every render) correctly tracks the real date. That
  // mismatch is what made the board show all of today's jobs under yesterday's column.
  const [todayStr, setTodayStr] = useState(() => toDateStr(new Date()));
  useEffect(() => {
    const iv = setInterval(() => {
      const t = toDateStr(new Date());
      setTodayStr(prev => (prev !== t ? t : prev));
    }, 60000);
    return () => clearInterval(iv);
  }, []);

  const { start, end } = useMemo(() => {
    if (RANGE_OPTIONS[rangeIdx].label === 'Custom') {
      return { start: customStart, end: customEnd };
    }
    return getRange(RANGE_OPTIONS[rangeIdx].days);
  }, [rangeIdx, customStart, customEnd, todayStr]);
  const { summary, capacity, unassignedJobs, jobs, loading, error } = useDispatchBoard(start, end, selectedBu);
  const currentAccount = myAccounts?.accounts?.find(a => a.id === myAccounts?.currentAccountId);
  const weather = useWeather(start, end, currentAccount?.name || '');

  const hiddenBUs = new Set(['Operations', 'Memberships', 'Gold Plan']);
  const [buUnits, setBuUnits] = useState([]);
  useEffect(() => {
    apiClient.get('/business-units').then(res => {
      const all = (res.data.units || []).filter(u => !hiddenBUs.has(u.name));
      // Deduplicate by name (Default Account has Walker + Francis with same BU names)
      const seen = new Set();
      const units = all.filter(u => {
        if (seen.has(u.name)) return false;
        seen.add(u.name);
        return true;
      });
      setBuUnits(units);
    }).catch(() => {});
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await apiClient.post('/dispatch/sync');
      window.location.reload();
    } catch (e) {
      console.error('Sync failed:', e);
    } finally {
      setSyncing(false);
    }
  };

  const days = useMemo(() => getDaysBetween(start, end), [start, end]);

  const selectedBuName = useMemo(() => {
    if (!selectedBu) return null;
    const u = buUnits.find(b => b.id === selectedBu);
    return u ? u.name : null;
  }, [selectedBu, buUnits]);

  // Build BU sections from capacity data + jobs
  const buSections = useMemo(() => {
    const sections = [];
    for (const cap of (capacity || [])) {
      const buName = cap.businessUnit.name;
      if (selectedBuName && buName.toLowerCase() !== selectedBuName.toLowerCase()) continue;
      const buIds = new Set(cap.businessUnit.ids || [cap.businessUnit.id]);
      const buJobs = (jobs || []).filter(j => buIds.has(j.localBuId));
      const capData = cap.capacity || {};
      sections.push({
        buName, buJobs, techs: cap.techs || [],
        buIds: Array.from(buIds),
        totalHours: capData.totalHours || 0,
        capacityByDay: capData.capacityByDay || null,
        availableHours: capData.availableHours || 0,
        bookedHours: capData.bookedHours || 0,
        capacitySource: capData.source || 'local',
      });
    }
    // Remaining jobs from unknown BUs (no localBuId means not mapped)
    const coveredBuIds = new Set((capacity || []).flatMap(c => c.businessUnit.ids || [c.businessUnit.id]));
    const remainingJobs = (jobs || []).filter(j => !j.localBuId || !coveredBuIds.has(j.localBuId));
    if (remainingJobs.length > 0 && !selectedBuName) {
      const byName = {};
      for (const j of remainingJobs) {
        const name = j.businessUnitName || `Unknown BU ${j.businessUnitId}`;
        if (!byName[name]) byName[name] = [];
        byName[name].push(j);
      }
      for (const [name, grpJobs] of Object.entries(byName)) {
        sections.push({ buName: name, buJobs: grpJobs, techs: [], totalHours: 0, capacityByDay: null, availableHours: 0, bookedHours: 0 });
      }
    }
    return sections;
  }, [capacity, jobs, selectedBuName]);

  const totalCapacity = (summary.totalTechs || 0) * days.length;
  const totalScheduled = summary.totalScheduled || 0;
  const totalInProgress = summary.totalInProgress || 0;
  const totalCompleted = summary.totalCompleted || 0;
  const totalNeedToBook = Math.max(0, totalCapacity - totalScheduled - totalInProgress - totalCompleted);

  return (
    <div>
      <Header title="Dispatch Board" />
      <div style={st.page}>
        <ErrorBanner message={error} />

        <div style={st.dateRow}>
          {RANGE_OPTIONS.map((opt, i) => (
            <button key={opt.label} onClick={() => setRangeIdx(i)}
              style={{ ...st.rangeBtn, ...(rangeIdx === i ? st.rangeBtnActive : {}) }}>
              {opt.label}
            </button>
          ))}

          {RANGE_OPTIONS[rangeIdx].label === 'Custom' && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: '0.5rem' }}>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={st.dateInput} />
              <span style={{ color: '#64748b' }}>to</span>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={st.dateInput} />
            </div>
          )}

          <button onClick={handleSync} disabled={syncing}
            style={{ ...st.rangeBtn, marginLeft: 'auto', background: syncing ? '#e2e8f0' : '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe' }}>
            {syncing ? 'Syncing...' : 'Sync ST Data'}
          </button>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
            {start} to {end} ({days.length} day{days.length !== 1 ? 's' : ''})
          </span>
        </div>

        {weather.days.length > 0 && (
          <div style={st.weatherStrip}>
            <div style={st.weatherCity}>{weather.city}</div>
            <div style={st.weatherDays}>
              {days.map(date => {
                const w = weather.days.find(d => d.date === date);
                return (
                  <div key={date} style={st.weatherCard}>
                    <div style={st.weatherDay}>{formatDayLabel(date).split(' ')[0]}</div>
                    <div style={st.weatherIcon}>{w ? weatherEmoji(w.code) : '—'}</div>
                    {w && <div style={st.weatherTemp}>{w.high}° / {w.low}°</div>}
                    {w && w.rain >= 30 && <div style={st.weatherRain}>💧 {w.rain}%</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={st.kpiRow}>
          <KPICard title="Capacity" value={fmtHours(summary.totalCapacityHours || 0)} icon="📋" color="#3b82f6" loading={loading} sub={`${summary.totalTechs || 0} techs × ${days.length} days`} />
          <KPICard title="Scheduled" value={fmtHours(summary.scheduledHours || 0)} icon="📅" color="#2563eb" loading={loading} sub={`${totalScheduled} jobs`} />
          <KPICard title="In Progress" value={fmtHours(summary.inProgressHours || 0)} icon="🔥" color="#f59e0b" loading={loading} sub={`${totalInProgress} jobs`} />
          <KPICard title="Completed" value={fmtHours(summary.completedHours || 0)} icon="✅" color="#22c55e" loading={loading} sub={`${totalCompleted} jobs`} />
          <KPICard title="Need to Book" value={fmtHours(Math.max(0, (summary.totalCapacityHours || 0) - (summary.scheduledHours || 0) - (summary.inProgressHours || 0) - (summary.completedHours || 0)))} icon="🚨" color={totalNeedToBook > 0 ? '#dc2626' : '#22c55e'} loading={loading} />
          <KPICard title="Revenue" value={formatCurrency(summary.totalRevenue || 0)} icon="💰" color="#ec4899" loading={loading} />
        </div>

        {loading ? <LoadingSpinner /> : (
          <>
            {buSections.length === 0 && (jobs || []).length === 0 ? (
              <div style={st.empty}>No dispatch data for this period. Jobs from ServiceTitan will appear here grouped by business unit.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {buSections.map((sec, idx) => (
                  <BuSection key={`${sec.buName}-${idx}`} buName={sec.buName} buJobs={sec.buJobs} techs={sec.techs} days={days} capacityByDay={sec.capacityByDay} totalHours={sec.totalHours} availableHours={sec.availableHours} bookedHours={sec.bookedHours} capacitySource={sec.capacitySource} buIds={sec.buIds || []} onCapacityUpdated={triggerRefresh} onSelectDay={(buName, date, dayJobs) => setScheduleModal({ buName, date, jobs: dayJobs })} />
                ))}
              </div>
            )}

          </>
        )}
      </div>

      {scheduleModal && (
        <DayScheduleModal
          buName={scheduleModal.buName}
          date={scheduleModal.date}
          jobs={scheduleModal.jobs}
          onClose={() => setScheduleModal(null)}
        />
      )}
    </div>
  );
}

const st = {
  page: { padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  dateRow: {
    display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
    background: '#fff', padding: '0.75rem 1rem', borderRadius: '0.75rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  rangeBtn: {
    padding: '0.45rem 0.875rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', color: '#475569',
    transition: 'all 0.15s',
  },
  rangeBtnActive: { background: '#6366f1', color: '#fff', border: '1px solid #6366f1' },
  kpiRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  weatherStrip: {
    background: '#fff', borderRadius: '0.75rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: '0.625rem 1rem', display: 'flex',
    alignItems: 'center', gap: '1rem', overflowX: 'auto',
  },
  weatherCity: {
    fontSize: '0.7rem', fontWeight: '700', color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0,
  },
  weatherDays: { display: 'flex', gap: '0.5rem', flex: 1 },
  weatherCard: { flex: '1 0 52px', textAlign: 'center', minWidth: '52px' },
  weatherDay:  { fontSize: '0.6rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase' },
  weatherIcon: { fontSize: '1.25rem', lineHeight: 1.3 },
  weatherTemp: { fontSize: '0.7rem', fontWeight: '600', color: '#1e293b' },
  weatherRain: { fontSize: '0.6rem', color: '#3b82f6' },
  buSection: {
    background: '#fff', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden',
  },
  buHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.875rem 1rem', cursor: 'pointer',
    background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
    borderBottom: '1px solid #c7d2fe', flexWrap: 'wrap', gap: '0.5rem',
  },
  techChip: {
    display: 'flex', alignItems: 'center', gap: '0.375rem',
    padding: '0.25rem 0.5rem', background: '#f0f0ff', border: '1px solid #e0e7ff',
    borderRadius: '999px',
  },
  techDot: {
    width: '22px', height: '22px', borderRadius: '50%', background: '#6366f1', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.65rem', flexShrink: 0,
  },
  dayCol: {
    flex: '0 0 160px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '0.5rem', overflow: 'hidden',
  },
  dayHeader: {
    padding: '0.375rem 0.5rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'center',
  },
  dayJobCard: {
    padding: '0.3rem 0.4rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.375rem',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' },
  th: {
    padding: '0.5rem 0.625rem', background: '#f8fafc', color: '#64748b', fontWeight: '600',
    fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left',
    borderBottom: '1px solid #e2e8f0',
  },
  td: { padding: '0.45rem 0.625rem', borderBottom: '1px solid #f1f5f9', color: '#1e293b' },
  empty: {
    padding: '3rem', textAlign: 'center', color: '#94a3b8', background: '#fff',
    borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  dateInput: {
    padding: '0.4rem 0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem',
    fontSize: '0.8rem', color: '#475569', outline: 'none', background: '#fff',
    cursor: 'pointer',
  },
  viewScheduleBtn: {
    margin: '0 0 0.625rem', padding: '0.4rem 0.75rem', background: '#eef2ff', color: '#6366f1',
    border: '1px solid #c7d2fe', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1.5rem',
  },
  modalCard: {
    background: '#fff', borderRadius: '0.75rem', width: '100%', maxWidth: '900px',
    maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0',
    background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
  },
  modalClose: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: '0.5rem',
    width: '28px', height: '28px', cursor: 'pointer', color: '#64748b', fontSize: '0.8rem',
  },
  sortToggleBtn: {
    padding: '0.25rem 0.6rem', background: '#f1f5f9', border: '1px solid #e2e8f0',
    borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.7rem', fontWeight: '600', color: '#475569',
  },
  sortToggleBtnActive: { background: '#6366f1', color: '#fff', border: '1px solid #6366f1' },
};
